import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './GuidePage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

const GUIDES = [
  {
    path: '/accuracy',
    title: 'How Accurate Should a Mechanical Watch Be?',
    blurb: 'Normal daily rates, what COSC / METAS / Superlative certification actually guarantees, and when poor accuracy means a service is due.',
  },
  {
    path: '/measure-watch-accuracy',
    title: 'How to Measure Watch Accuracy at Home',
    blurb: 'Three ways to get a trustworthy seconds-per-day number — from a free manual method to listening to the movement itself.',
  },
  {
    path: '/watch-running-fast',
    title: 'Why Is My Watch Running Fast or Slow?',
    blurb: 'Magnetism, service wear, and position effects: how to tell which one your watch has and what to do about it.',
  },
  {
    path: '/what-is-a-timegrapher',
    title: 'What Is a Timegrapher? Readings Explained',
    blurb: 'Rate, amplitude, and beat error — what each reading means, healthy ranges, and hardware vs. app accuracy.',
  },
  {
    path: '/brand-accuracy',
    title: 'Watch Accuracy by Brand: Rolex, Omega, Seiko',
    blurb: 'Published accuracy specs by brand and movement tier, and what owners actually measure in practice.',
  },
  {
    path: '/beat-error',
    title: 'Beat Error Explained',
    blurb: 'What the tick-tock asymmetry means, acceptable ranges by movement, and whether it can be fixed at home.',
  },
  {
    path: '/timegrapher',
    title: 'Building a Timegrapher From a Phone Microphone',
    blurb: 'The engineering deep-dive: epoch folding, autocorrelation, and how a phone mic hears a watch escapement.',
  },
  {
    path: '/accuracy-reports',
    title: 'Watch Accuracy Reports — How to Read Them',
    blurb: 'What ChronoLog accuracy reports show and how to interpret the numbers.',
  },
];

function GuidesPage() {
  usePageTracking('guides_index', {
    page_title: 'Watch Accuracy Guides - ChronoLog',
  });

  return (
    <div className="guide-page">
      <Helmet>
        <title>Watch Accuracy Guides | ChronoLog</title>
        <meta
          name="description"
          content="Practical guides to mechanical watch accuracy: normal daily rates, timegrapher readings, beat error, brand specs, and how to measure your own watch."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Watch Accuracy Guides | ChronoLog" />
        <meta
          property="og:description"
          content="Practical guides to mechanical watch accuracy: normal daily rates, timegrapher readings, beat error, brand specs, and how to measure your own watch."
        />
        <meta property="og:url" content="https://www.chronolog.watch/guides" />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <link rel="canonical" href="https://www.chronolog.watch/guides" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Watch Accuracy Guides',
            url: 'https://www.chronolog.watch/guides',
            publisher: {
              '@type': 'Organization',
              name: 'ChronoLog',
              url: 'https://www.chronolog.watch',
            },
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: GUIDES.map((g, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: g.title,
                url: `https://www.chronolog.watch${g.path}`,
              })),
            },
          })}
        </script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>Watch Accuracy Guides</h1>
        <p className="article-lead">
          Everything you need to understand, measure, and improve the timekeeping of a
          mechanical watch &mdash; written to answer the questions collectors actually ask.
        </p>

        {GUIDES.map((g) => (
          <div className="pipeline-step" key={g.path}>
            <h3>
              <Link to={g.path}>{g.title}</Link>
            </h3>
            <p>{g.blurb}</p>
          </div>
        ))}
      </article>
    </div>
  );
}

export default GuidesPage;
